import { useEffect } from "react";

import { usePublicPrimaryConfiguration } from "@/hooks/useConfig";

const resolveAssetUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;

  const apiBase = import.meta.env.VITE_API_BASE_URL || "/api";
  try {
    const base = new URL(apiBase, window.location.origin);
    return `${base.origin}${url.startsWith("/") ? "" : "/"}${url}`;
  } catch {
    return url;
  }
};

/**
 * Applies dynamic PWA branding (title, favicon, manifest) based on public
 * brand configuration fetched from the API.
 *
 * Call this hook once at the top-most component level (e.g. AppBootstrap) so
 * that branding is applied on every route, including auth pages that are
 * rendered outside of the layout that contains the Header component.
 */
export const useBrandingEffect = () => {
  const { data: publicConfig, isLoading: isBrandLoading } =
    usePublicPrimaryConfiguration();

  const brandName = publicConfig?.brand_name?.trim() || "";
  const brandFavicon = resolveAssetUrl(publicConfig?.favicon_url);

  useEffect(() => {
    if (isBrandLoading) return;

    if (brandName) {
      document.title = brandName;
      const titlePlaceholder = document.querySelector<HTMLTitleElement>(
        "#my-title-placeholder",
      );
      if (titlePlaceholder) {
        titlePlaceholder.textContent = brandName;
      }

      const appleTitle = document.querySelector<HTMLMetaElement>(
        "#my-apple-title-placeholder",
      );
      if (appleTitle) {
        appleTitle.content = brandName;
      }
    }

    if (brandFavicon) {
      let favicon = document.querySelector<HTMLLinkElement>(
        "#my-favicon-placeholder",
      );
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = brandFavicon;

      const appleIcon = document.querySelector<HTMLLinkElement>(
        "#my-apple-icon-placeholder",
      );
      if (appleIcon) {
        appleIcon.href = brandFavicon;
      }

      const appleIconPrecomposed = document.querySelector<HTMLLinkElement>(
        "#my-apple-icon-precomposed-placeholder",
      );
      if (appleIconPrecomposed) {
        appleIconPrecomposed.href = brandFavicon;
      }
    }

    // Dynamic Manifest for PWA
    const mimeType =
      brandFavicon?.toLowerCase().endsWith(".jpg") ||
      brandFavicon?.toLowerCase().endsWith(".jpeg")
        ? "image/jpeg"
        : brandFavicon?.toLowerCase().endsWith(".svg")
          ? "image/svg+xml"
          : brandFavicon?.toLowerCase().endsWith(".webp")
            ? "image/webp"
            : "image/png";

    const manifest = {
      name: brandName || "Loyalty",
      short_name: brandName || "Loyalty",
      description: brandName
        ? `${brandName} Application`
        : "Loyalty Application",
      start_url: "/",
      display: "standalone",
      background_color: "#ffffff",
      theme_color: "#ffffff",
      icons: [
        {
          src: brandFavicon || "/logo.png?v=3",
          sizes: "192x192",
          type: mimeType,
          purpose: "any",
        },
        {
          src: brandFavicon || "/logo.png?v=3",
          sizes: "512x512",
          type: mimeType,
          purpose: "maskable",
        },
        {
          src: brandFavicon || "/logo.png?v=3",
          sizes: "180x180",
          type: mimeType,
          purpose: "any",
        },
      ],
    };

    const stringManifest = JSON.stringify(manifest);
    const blob = new Blob([stringManifest], { type: "application/json" });
    const manifestURL = URL.createObjectURL(blob);

    const manifestLink = document.querySelector("#my-manifest-placeholder");
    if (manifestLink) {
      manifestLink.setAttribute("href", manifestURL);
    }

    // Cleanup blob URL on unmount or on subsequent runs to avoid memory leak
    return () => {
      URL.revokeObjectURL(manifestURL);
    };
  }, [brandFavicon, brandName, isBrandLoading]);
};
